// src/pages/PrivacyPolicy.jsx
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles/LegalPages.css';

const LAST_UPDATED = 'April 2, 2026';
const COMPANY      = 'Aucwini Technology LLP';
const BRAND        = 'Caryugo';
const EMAIL        = 'privacy@caryugo.com';

const Section = ({ id, title, children }) => (
  <section className="lp-section" id={id}>
    <h2 className="lp-section-title">{title}</h2>
    <div className="lp-section-body">{children}</div>
  </section>
);

const PrivacyPolicy = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="lp-page">
      {/* Hero */}
      <div className="lp-hero">
        <div className="lp-hero-inner">
          <div className="lp-hero-badge">Legal</div>
          <h1 className="lp-hero-title">Privacy Policy</h1>
          <p className="lp-hero-meta">Last updated: {LAST_UPDATED}</p>
          <p className="lp-hero-sub">
            Your privacy matters to us. This policy explains what data we collect,
            how we use it, and your rights — in plain language.
          </p>
        </div>
      </div>

      <div className="lp-layout">
        {/* Sidebar TOC */}
        <aside className="lp-sidebar">
          <div className="lp-toc">
            <p className="lp-toc-label">Contents</p>
            <nav>
              {[
                ['#overview',     '1. Overview'],
                ['#collection',   '2. Data We Collect'],
                ['#how-we-use',   '3. How We Use Data'],
                ['#sharing',      '4. Sharing of Data'],
                ['#cookies',      '5. Cookies & Tracking'],
                ['#retention',    '6. Data Retention'],
                ['#security',     '7. Security'],
                ['#rights',       '8. Your Rights'],
                ['#children',     '9. Children\'s Privacy'],
                ['#third-party',  '10. Third-Party Links'],
                ['#changes',      '11. Policy Changes'],
                ['#contact',      '12. Contact Us'],
              ].map(([href, label]) => (
                <a key={href} href={href} className="lp-toc-link">{label}</a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main content */}
        <main className="lp-main">
          <Section id="overview" title="1. Overview">
            <p>
              {COMPANY} ("we", "us", "our") operates the <strong>{BRAND}</strong> platform.
              This Privacy Policy describes how we collect, use, store, share, and protect
              personal information when you use our website, mobile applications, and related services.
            </p>
            <p>
              This policy is compliant with the <strong>Information Technology Act, 2000</strong>,
              the <strong>Information Technology (Reasonable Security Practices and Procedures and
              Sensitive Personal Data or Information) Rules, 2011</strong>, and applicable data
              protection principles in India.
            </p>
            <p>
              By using {BRAND}, you consent to the practices described in this policy.
            </p>
          </Section>

          <Section id="collection" title="2. Data We Collect">
            <p>We collect the following categories of information:</p>

            <h3 className="lp-sub-heading">A. Information You Provide</h3>
            <ul className="lp-list">
              <li><strong>Identity Data:</strong> Name, date of birth, government ID (Aadhaar, PAN, Driving Licence).</li>
              <li><strong>Contact Data:</strong> Mobile number, email address, residential address.</li>
              <li><strong>Vehicle Data:</strong> Registration number, make, model, year, mileage, photos, service history.</li>
              <li><strong>Transaction Data:</strong> Booking amounts, payment history, transaction IDs.</li>
              <li><strong>Communication Data:</strong> Messages sent through our platform, support tickets, reviews.</li>
            </ul>

            <h3 className="lp-sub-heading">B. Information Collected Automatically</h3>
            <ul className="lp-list">
              <li><strong>Device Data:</strong> IP address, device type, operating system, browser type.</li>
              <li><strong>Usage Data:</strong> Pages visited, features used, time spent, click patterns.</li>
              <li><strong>Location Data:</strong> City-level location when you use city-specific features (with your consent).</li>
              <li><strong>Log Data:</strong> Server logs, error reports, and diagnostic information.</li>
            </ul>

            <h3 className="lp-sub-heading">C. Information from Third Parties</h3>
            <ul className="lp-list">
              <li>Payment processors (transaction status, payment method type — not card numbers).</li>
              <li>VAHAN database for RC and vehicle verification.</li>
              <li>Credit bureaus (for financing applicants, with your explicit consent).</li>
            </ul>
          </Section>

          <Section id="how-we-use" title="3. How We Use Your Data">
            <p>We use your personal data for the following purposes:</p>
            <ul className="lp-list">
              <li><strong>Account Management:</strong> Creating and managing your {BRAND} account.</li>
              <li><strong>OTP Authentication:</strong> Sending one-time passwords via SMS through Twilio for secure login.</li>
              <li><strong>Transactions:</strong> Processing purchases, bookings, test drives, and sell requests.</li>
              <li><strong>Verification:</strong> Verifying your identity and vehicle ownership documents.</li>
              <li><strong>Communication:</strong> Sending service updates, booking confirmations, and transaction alerts.</li>
              <li><strong>Customer Support:</strong> Responding to your queries and resolving disputes.</li>
              <li><strong>Safety &amp; Security:</strong> Detecting fraud, preventing abuse, and enforcing our Terms.</li>
              <li><strong>Product Improvement:</strong> Analysing usage patterns to improve features and performance.</li>
              <li><strong>Legal Compliance:</strong> Meeting obligations under applicable Indian law.</li>
              <li><strong>Marketing:</strong> Sending promotional offers and new listings (only with your consent; you may opt out at any time).</li>
            </ul>
          </Section>

          <Section id="sharing" title="4. Sharing of Data">
            <p>
              We do <strong>not sell</strong> your personal data. We share it only in the following circumstances:
            </p>
            <ul className="lp-list">
              <li>
                <strong>Buyers &amp; Sellers:</strong> When a transaction is initiated, relevant contact
                and vehicle details are shared between the parties to facilitate the sale.
              </li>
              <li>
                <strong>Service Providers:</strong> Trusted partners who process data on our behalf —
                including Twilio (SMS), payment gateways, cloud hosting providers, and email services —
                under strict data processing agreements.
              </li>
              <li>
                <strong>Inspection Partners:</strong> Certified inspectors receive vehicle and appointment
                details to conduct inspections.
              </li>
              <li>
                <strong>Lending Partners:</strong> With your explicit consent, financial data is shared
                with lending institutions for loan processing.
              </li>
              <li>
                <strong>Legal Authorities:</strong> When required by law, court order, or regulatory
                authority under Indian law.
              </li>
              <li>
                <strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of
                assets, user data may be transferred as part of that transaction.
              </li>
            </ul>
          </Section>

          <Section id="cookies" title="5. Cookies & Tracking Technologies">
            <p>
              We use cookies, local storage, and similar tracking technologies to:
            </p>
            <ul className="lp-list">
              <li>Keep you logged in and maintain session state.</li>
              <li>Remember your city preference and search filters.</li>
              <li>Analyse platform usage through analytics tools.</li>
              <li>Serve relevant listings and features.</li>
            </ul>
            <p>
              You may disable cookies through your browser settings. However, some features
              (including login) may not function correctly without cookies.
            </p>
            <p>
              We do not use cookies for cross-site advertising or share cookie data with
              third-party advertisers.
            </p>
          </Section>

          <Section id="retention" title="6. Data Retention">
            <p>
              We retain your personal data for as long as your account is active or as needed
              to provide services. Specifically:
            </p>
            <ul className="lp-list">
              <li><strong>Account data</strong> is retained for the life of your account plus 3 years after closure.</li>
              <li><strong>Transaction records</strong> are retained for 7 years as required by financial regulations.</li>
              <li><strong>OTP logs</strong> are deleted within 24 hours of use.</li>
              <li><strong>Marketing preferences</strong> are retained until you withdraw consent.</li>
            </ul>
            <p>
              Upon your request for deletion, we will anonymise or delete your data within
              30 days, except where we are legally required to retain it.
            </p>
          </Section>

          <Section id="security" title="7. Security">
            <p>
              We implement industry-standard security measures to protect your data:
            </p>
            <ul className="lp-list">
              <li>HTTPS/TLS encryption for all data in transit.</li>
              <li>AES-256 encryption for sensitive data at rest.</li>
              <li>JWT-based authentication with token expiry.</li>
              <li>OTP-based two-factor authentication for all accounts.</li>
              <li>Regular security audits and penetration testing.</li>
              <li>Role-based access controls limiting employee access to user data.</li>
            </ul>
            <p>
              While we take all reasonable precautions, no system is 100% secure.
              In the event of a data breach, we will notify affected users as required by applicable law.
            </p>
          </Section>

          <Section id="rights" title="8. Your Rights">
            <p>
              Under applicable Indian data protection principles, you have the right to:
            </p>
            <ul className="lp-list">
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
              <li><strong>Correction:</strong> Update or correct inaccurate personal information.</li>
              <li><strong>Deletion:</strong> Request deletion of your account and associated data (subject to legal obligations).</li>
              <li><strong>Portability:</strong> Receive your data in a machine-readable format.</li>
              <li><strong>Withdraw Consent:</strong> Opt out of marketing communications at any time.</li>
              <li><strong>Grievance Redressal:</strong> Lodge a complaint with our Grievance Officer (details below).</li>
            </ul>
            <p>
              To exercise any of these rights, contact us at{' '}
              <a href="mailto:privacy@caryugo.com">privacy@caryugo.com</a>.
              We will respond within 30 days.
            </p>
          </Section>

          <Section id="children" title="9. Children's Privacy">
            <p>
              {BRAND} is not intended for users under the age of 18. We do not knowingly
              collect personal information from minors. If we discover that a minor has
              provided us with personal data, we will delete it promptly.
            </p>
            <p>
              If you are a parent or guardian and believe your child has registered on our
              platform, please contact us immediately at{' '}
              <a href={`mailto:${EMAIL}`}>{EMAIL}</a>.
            </p>
          </Section>

          <Section id="third-party" title="10. Third-Party Links">
            <p>
              Our platform may contain links to third-party websites, financial partners,
              or government portals (such as VAHAN or Parivahan). This Privacy Policy does
              not apply to those external sites. We encourage you to review the privacy
              policies of any third-party sites you visit.
            </p>
          </Section>

          <Section id="changes" title="11. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time to reflect changes in
              our practices or applicable law. When we make material changes, we will:
            </p>
            <ul className="lp-list">
              <li>Update the "Last Updated" date at the top of this policy.</li>
              <li>Send a notification to your registered mobile number or email.</li>
              <li>Display a prominent notice on the platform for 30 days.</li>
            </ul>
            <p>
              Continued use of the platform after changes take effect constitutes acceptance
              of the updated policy.
            </p>
          </Section>

          <Section id="contact" title="12. Contact & Grievance Officer">
            <div className="lp-contact-card">
              <div className="lp-contact-row">
                <span className="lp-contact-icon">🏢</span>
                <div>
                  <strong>Grievance Officer — {COMPANY}</strong><br />
                  Indore, Madhya Pradesh, India
                </div>
              </div>
              <div className="lp-contact-row">
                <span className="lp-contact-icon">✉️</span>
                <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
              </div>
              <div className="lp-contact-row">
                <span className="lp-contact-icon">⏱️</span>
                <span>Response time: within 30 days of receipt</span>
              </div>
            </div>
          </Section>

          {/* Footer nav */}
          <div className="lp-footer-nav">
            <Link to="/terms" className="lp-footer-link">Terms &amp; Conditions →</Link>
            <Link to="/"      className="lp-footer-link">← Back to Home</Link>
          </div>
        </main>
      </div>
    </div>
  );
};

export default PrivacyPolicy;